/**
 * @file services/companies.js
 * @since v1.0
 * @author Carlos Lenon <code@carloslenon.com>
 */

// #region common
import { CapTableVersion } from 'common/types/company';
import { MeasurementDate } from 'common/types/measurementDate';
import BaseServiceRetryWrapper from './baseRetry';
import { COMPANY, MEASUREMENT_DATES } from '../common/endpoints';
import { GetCompanySummaryByMDResponse } from './hooks/companies/useGetCompanySummaryByMD/types';
// #endregion

class CompaniesService extends BaseServiceRetryWrapper {
  getCapTableList = (
    companyId: string,
    {
      companyMeasurementDateId,
      measurementDateId,
    }: {
      companyMeasurementDateId?: string;
      measurementDateId?: string;
    }
  ): Promise<CapTableVersion[]> =>
    this.get(
      COMPANY.getCapTableList(companyId, {
        companyMeasurementDateId,
        measurementDateId,
      })
    ) as Promise<CapTableVersion[]>;

  /**
   * @function
   * @name getCompanySummary
   * @description Get investments grouped by fund for the given company
   */
  getCompanySummary = (companyId: number, measurementDateId?: number): Promise<GetCompanySummaryByMDResponse> =>
    this.get(COMPANY.getCompanySummary(companyId, measurementDateId)) as Promise<GetCompanySummaryByMDResponse>;

  /**
   * @function
   * @name hasCompanySummary
   * @description Get investments grouped by fund for the given company
   */
  hasCompanySummary = (companyId: number, measurementDateId?: number) =>
    this.get(COMPANY.hasCompanySummary(companyId, measurementDateId));

  /**
   * @function
   * @name getFundsByCompanyId
   * @description Get the funds/tagalines by id that belongs to a company
   */
  getFundsByCompanyId = (companyId: string) => this.get(COMPANY.getFundsByCompanyId(companyId));

  /**
   * @function
   * @name getVersionsByCompanyIdByDateId
   * @description update captable by id selected.
   * @param {integer} companyId the company id
   * @param {integer} dateId the date id
   * @param {boolean} includeConvNotes include convertible notes
   * @returns {array}
   */
  getVersionsByCompanyIdByDateId = (companyId: number, dateId: number, includeConvNotes: boolean) => this.get(COMPANY.getVersionsByCompanyIdByDateId(companyId, dateId, includeConvNotes));

  /**
   * @function
   * @name getAllocationsVersionsByCompanyIdByDateId
   * @description get allocations versions by company and by date.
   * @param {integer} companyId the company id
   * @param {integer} dateId the date id
   */
  getAllocationsVersionsByCompanyIdByDateId = (companyId: string, dateId: string) =>
    this.get(COMPANY.getAllocationsVersionsByCompanyIdByDateId(companyId, dateId));

  /**
   * @function
   * @name measurementDates
   * @description get companies measurement dates.
   * @param {INTEGER} companyId the company id
   */
  measurementDates = (companyId: number): Promise<MeasurementDate[]> =>
    this.get(MEASUREMENT_DATES.measurementDatesByCompanyId(companyId)) as Promise<MeasurementDate[]>;

  /**
   * @function
   * @name getAllocationValuesByCompanyId
   * @description Get Allocations Values by Company and Measurement Date
   * @param {integer} companyId The Company ID
   * @param {integer} dateId The Measurement Date ID
   */
  getAllocationValuesByCompanyId = (companyId: string, measurementDateId: string, version: string) =>
    this.get(COMPANY.getAllocationValuesByCompanyId(companyId, measurementDateId, version));

  /* get the data that will be set to companyInfo in store.
  Use same URL as update endpoint ( /api/companies/<companyId> ) */
  getCompanyById = (companyId: number) => this.get(COMPANY.updateById(companyId));

  getFiscalYearData = (companyMeasurementDateId: string) =>
    this.get(COMPANY.getQuarterEndsByCompanyId(companyMeasurementDateId));

  /**
   * @function
   * @name getCompanyMetric
   * @description Get a specific metric for a company
   * @param {integer} companyId The Company ID
   * @param {string} metricName The metric name (e.g., 'invested-capital', 'unrealized-value', 'moic')
   * @param {string} measurementDateSlug Optional measurement date slug for filtering
   */
  getCompanyMetric = <T = unknown>(companyId: number, metricName: string, measurementDateSlug?: string): Promise<T> =>
    this.get(COMPANY.getCompanyMetric(companyId, metricName, measurementDateSlug)) as Promise<T>;

  getDocumentGenerationJobs = (
    companyId: string,
    page?: number,
    pageSize?: number,
    status?: string,
    processingType?: string
  ) =>
    this.get(COMPANY.getDocumentGenerationJobs(companyId, page, pageSize, status, processingType));
}

export default CompaniesService;
